import { TextButton } from '@creditornot/cb-button';
import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Item } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { usePluralization } from 'common/hooks';
import { MultiImageFileError } from 'modules/menu/components/ImageUploadInput/MultiImageUploadInput/MultiImageFileSelector';
import { MIN_HEIGHT, MIN_WIDTH } from 'modules/menu/components/ImageUploadInput/constants';
import { getImageSize } from 'modules/menu/components/ImageUploadInput/utils';
import { ITEM_MAX_IMAGE_COUNT } from 'modules/menu/constants';
import { Offer } from 'modules/shared-menu/pages/SharedMenuEditorPage/types';

import { useImageUpload } from './useImageUpload';

type Props = {
  children: (data: { imageHoverActive: boolean; uploadingImages: boolean }) => React.ReactNode;
  existingImageUrls: string[];
  item: Item | Offer;
  menuId: string;
  onUpdateMenuData: (item: Item | Offer) => void;
  onDrop?: (files: File[]) => void;
  disableDropzone?: boolean;
  type: 'shared-menu' | 'venue-menu';
};

const Root = styled.div`
  position: relative;
`;

const ToastContentRoot = styled.div`
  display: flex;
  flex-direction: column;
`;

const UndoButtonContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
`;

const ToastContentWithUndoButton = ({ children, onUndo }: { children: React.ReactNode; onUndo: () => void }) => {
  return (
    <ToastContentRoot>
      {children}
      <UndoButtonContainer>
        <TextButton variant="grey" onClick={onUndo}>
          <FormattedMessage id="state.undo" />
        </TextButton>
      </UndoButtonContainer>
    </ToastContentRoot>
  );
};

const FailedImagesListRoot = styled.div`
  display: flex;
  flex-direction: column;
  margin-block-start: 8px;
`;

const ErrorGroup = styled.ul`
  margin: 0;
  padding: 0;
  list-style-position: inside;
`;

const ErrorListItem = styled.li`
  overflow-wrap: anywhere;
`;

const FailedImagesList = ({ errors }: { errors: MultiImageFileError[] }) => {
  const sizeErrors = errors.filter(error => error.type === 'error-image-too-small');
  const unknownErrors = errors.filter(error => error.type === 'error-unknown');

  return (
    <FailedImagesListRoot>
      {sizeErrors.length > 0 && (
        <ErrorGroup style={{ marginBlockEnd: '4px' }}>
          <FormattedMessage
            id="image-upload.errorTooSmall"
            values={{
              width: MIN_WIDTH,
              height: MIN_HEIGHT,
            }}
          />
          {sizeErrors.map((error, index) => (
            <ErrorListItem key={index}>{error.fileName}</ErrorListItem>
          ))}
        </ErrorGroup>
      )}
      {unknownErrors.length > 0 && (
        <ErrorGroup>
          <FormattedMessage id="errors.unknown-error-occurred" />
          {unknownErrors.map((error, index) => (
            <ErrorListItem key={index}>{error.fileName}</ErrorListItem>
          ))}
        </ErrorGroup>
      )}
    </FailedImagesListRoot>
  );
};

type FileDropResult = 'success' | 'partial-success' | 'error';

const getFileDropResult = (validFiles: File[], fileErrors: MultiImageFileError[]): FileDropResult => {
  if (fileErrors.length === 0) {
    return 'success';
  }
  if (validFiles.length === 0) {
    return 'error';
  }
  return 'partial-success';
};

const showErrorToastOverMaxCount = () =>
  toast.error(
    <FormattedMessage
      id="image-upload.listHeaderUpload.errors.over-max-amount"
      values={{ count: ITEM_MAX_IMAGE_COUNT }}
    />,
  );

const showErrorToastAllFailed = (fileErrors: MultiImageFileError[]) =>
  toast.error(<FailedImagesList errors={fileErrors} />);

const HeaderImageUpload = ({
  children,
  existingImageUrls,
  item,
  onUpdateMenuData,
  onDrop,
  menuId,
  type,
  disableDropzone = false,
}: Props) => {
  const { FormattedPluralMessage } = usePluralization();
  const handleSuccessNotification = useCallback(
    (
      fileDropResult: 'success' | 'partial-success',
      validFiles: File[],
      fileErrors: MultiImageFileError[],
      onUndo: () => Promise<void>,
    ) => {
      const toastId = String(Math.random());

      const handleUndoClick = () => {
        toast.remove(toastId);
        toast.promise(onUndo(), {
          loading: <FormattedMessage id="image-upload.listHeaderUpload.undo.undoChangesSubmitting" />,
          success: <FormattedMessage id="image-upload.listHeaderUpload.undo.undoSuccessDescription" />,
          error: <FormattedMessage id="image-upload.listHeaderUpload.undo.undoErrorDescription" />,
        });
      };

      if (fileDropResult === 'success') {
        toast.success(
          <ToastContentWithUndoButton onUndo={handleUndoClick}>
            <FormattedPluralMessage id="image-upload.success" count={validFiles.length} />
          </ToastContentWithUndoButton>,
          {
            duration: 10000,
            id: toastId,
          },
        );
      } else {
        toast(
          <ToastContentWithUndoButton onUndo={handleUndoClick}>
            <FormattedMessage
              id="image-upload.listHeaderUpload.errors.partial-success"
              values={{ successCount: validFiles.length, errorCount: fileErrors.length }}
            />
            <FailedImagesList errors={fileErrors} />
          </ToastContentWithUndoButton>,
          {
            duration: 20000,
            id: toastId,
          },
        );
      }
    },
    [FormattedPluralMessage],
  );

  const { uploadImages, updateItemOrOffer, isUploadingImages, isUpdatingItem } = useImageUpload({
    existingImageUrls,
    item,
    menuId,
    type,
  });

  const handleSelectFiles = useCallback(
    async (files: File[]) => {
      if (existingImageUrls.length + files.length > ITEM_MAX_IMAGE_COUNT) {
        showErrorToastOverMaxCount();
        return;
      }

      const validFileNames: string[] = [];
      const fileErrors: MultiImageFileError[] = [];

      await Promise.all(
        files.map(async file => {
          try {
            const imageSize = await getImageSize(file);
            if (imageSize.width < MIN_WIDTH || imageSize.height < MIN_HEIGHT) {
              fileErrors.push({ fileName: file.name, type: 'error-image-too-small' });
            } else {
              validFileNames.push(file.name);
            }
          } catch (_) {
            fileErrors.push({ fileName: file.name, type: 'error-unknown' });
          }
        }),
      );

      const validFiles = files.filter(file => validFileNames.includes(file.name));
      const fileDropResult = getFileDropResult(validFiles, fileErrors);

      if (fileDropResult === 'success' || fileDropResult === 'partial-success') {
        const updatedItem = await uploadImages(validFiles);
        onUpdateMenuData(updatedItem);
        handleSuccessNotification(fileDropResult, validFiles, fileErrors, async () => {
          const revertedItem = await updateItemOrOffer({ item: updatedItem, imageUrls: existingImageUrls });
          onUpdateMenuData(revertedItem);
        });
      } else {
        showErrorToastAllFailed(fileErrors);
      }
    },
    [existingImageUrls, updateItemOrOffer, uploadImages, handleSuccessNotification, onUpdateMenuData],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/jpeg': [],
      'image/png': [],
    },
    multiple: true,
    onDropAccepted: onDrop || handleSelectFiles,
    useFsAccessApi: false,
    noClick: true,
    noKeyboard: true,
    disabled: disableDropzone,
  });

  return (
    <Root {...getRootProps()}>
      <input {...getInputProps()} />
      {children({ imageHoverActive: isDragActive, uploadingImages: isUploadingImages || isUpdatingItem })}
    </Root>
  );
};

export default React.memo(HeaderImageUpload);
